import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AccountService } from '../account/account.service';
import { SettingsService } from '../account/settings.service';
import { NavigationService } from '../core/navigation.service';
import { WaypointService } from '../core/waypoint.service';
import { VehicleService } from '../vehicle/vehicle.service';
import { ECarSettings } from '../../models/ecar';
import { ECarType } from '../../models/ecar-type';
import { DisplayMode } from '../../models/display-mode';

export interface VehicleToolResult {
  output: Record<string, unknown>;
  stateChanged: boolean;
}

/** Same order as the vehicle options dialog: index + 1 is the backend charger type id. */
const CHARGER_TYPES = ['CCS', 'CHADEMO', 'SCHUKO', 'TESLA', 'TYPE 1', 'TYPE 2'];

/** The dialog's five driving behaviour steps and their stored 0-100 userBehavior values. */
const DRIVING_STYLES: Record<string, number> = {
  efficiency_focused: 0,
  smooth: 25,
  balanced: 50,
  dynamic: 75,
  performance_oriented: 100
};

/** Slider ranges of the vehicle options dialog. */
const RANGES = {
  battery_level: [1, 100],
  passengers: [1, 5],
  tire_pressure_bar: [1.5, 2.6],
  speed_limiter_kmh: [80, 130],
  min_battery_at_destination: [10, 75],
  battery_reserve_range_mode: [20, 50],
  battery_reserve_route_mode: [10, 30],
  battery_state_of_health: [50, 110],
  fast_charge_limit: [75, 90]
} as const;

type RangeKey = keyof typeof RANGES;

const NAVIGATION_SLIDER_HOLD_MS = 15000;

/**
 * Voice assistant access to the active car's parameters and the routing options of the vehicle
 * options dialog. Changes are persisted the same way the dialog persists them (backend for
 * signed-in users, VehicleService + localStorage otherwise). Switching, adding, editing and
 * removing cars is intentionally not available by voice.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeVehicleToolsService {
  private readonly accountService = inject(AccountService);
  private readonly settingsService = inject(SettingsService);
  private readonly navigationService = inject(NavigationService);
  private readonly waypointService = inject(WaypointService);
  private readonly vehicleService = inject(VehicleService);

  private navigationSliderHoldRef: ReturnType<typeof setTimeout> | null = null;

  private static readonly TOOL_NAMES = new Set(['get_vehicles', 'set_vehicle_settings']);

  public handles(name: string): boolean {
    return RealtimeVehicleToolsService.TOOL_NAMES.has(name);
  }

  public async execute(name: string, args: any): Promise<VehicleToolResult> {
    try {
      switch (name) {
        case 'get_vehicles': return { output: this.buildVehicleState(), stateChanged: false };
        case 'set_vehicle_settings': return await this.setVehicleSettings(args);
      }
    } catch (error: any) {
      console.error('Vehicle tool failed:', name, error);
      return this.fail('request_failed', error?.message || 'The change could not be saved. Please try again.');
    }
    return this.fail('unknown_tool', `Unknown vehicle tool ${name}.`);
  }

  /** Active vehicle and its settings; also used in the session prompt. */
  public buildVehicleState(): Record<string, unknown> {
    const authorized = this.accountService.getIsAuthorized();
    const car = this.vehicleService.getSelectedCar();
    const activeUserCar = authorized ? this.accountService.getUserProfile()?.getSelectedUserCar() : null;
    const units = this.settingsService.getUnit();

    return {
      signed_in: authorized,
      navigating: this.isNavigating(),
      display_mode: this.waypointService.getSelectedDisplayMode(),
      user_units: { distance: units.Distance, speed: units.Speed, pressure: units.Pressure },
      active_vehicle: car ? {
        name: activeUserCar?.name || car.Name,
        model: car.Name,
        model_id: car.Id,
        factory_range_km: car.FactoryRange,
        supports_fast_charging: car.FastChargePower !== 0
      } : null,
      settings: this.describeSettings(this.vehicleService.getECarSettings(), this.vehicleService.getChargingStations())
    };
  }

  public getToolDefinitions(): any[] {
    const range = (key: RangeKey) => `${RANGES[key][0]}-${RANGES[key][1]}`;
    return [
      {
        type: "function",
        name: "get_vehicles",
        description: "Read the active vehicle and all of its settings (as in the vehicle options dialog), including routing options.",
        parameters: { type: "object", properties: {} }
      },
      {
        type: "function",
        name: "set_vehicle_settings",
        description: "Change the active vehicle's parameters and routing options; pass only what the user wants to change. The route or range is recalculated automatically.",
        parameters: {
          type: "object",
          properties: {
            battery_level: {
              type: "number",
              description: `The car's current battery / state of charge in % (${range('battery_level')}). DEFAULT for any plain battery request: "set battery to 80", "battery is 60 percent", "I have 45% charge".`
            },
            passengers: { type: "number", description: `Number of people in the car (${range('passengers')}).` },
            tire_pressure_bar: { type: "number", description: `Tire pressure in bar (${range('tire_pressure_bar')}, standard 2.3); convert from psi.` },
            driving_style: { type: "string", enum: Object.keys(DRIVING_STYLES), description: "Driving behaviour." },
            speed_limiter_kmh: { type: "number", description: `Speed limiter in km/h (${range('speed_limiter_kmh')}); convert from mph. Used for range.` },
            min_battery_at_destination: { type: "number", description: `Minimum battery on arrival in % (${range('min_battery_at_destination')}). Used for routes.` },
            battery_reserve: {
              type: "number",
              description: `Safety reserve (buffer) in %, NOT the current battery level: use ONLY when the user explicitly says "reserve", "safety limit" or "buffer"; for plain "battery" use battery_level. The app has a separate reserve per mode and applies this to the CURRENT mode unless battery_reserve_mode is given: range mode ${range('battery_reserve_range_mode')} (50 = return limit), route mode ${range('battery_reserve_route_mode')}.`
            },
            battery_reserve_mode: {
              type: "string",
              enum: ["range", "route"],
              description: "Only when the user explicitly names the mode whose reserve to change; otherwise omit."
            },
            battery_state_of_health: { type: "number", description: `Battery state of health / range adjustment in % (${range('battery_state_of_health')}).` },
            fast_charge_limit: { type: "number", description: `Fast charging stops at this % (${range('fast_charge_limit')}); only for cars that support fast charging.` },
            charger_types: {
              type: "array",
              items: { type: "string", enum: CHARGER_TYPES },
              description: "The complete list of plug types the car can use (replaces the current list; at least one)."
            },
            trip_recording: { type: "boolean", description: "Record trips." },
            toll_roads: { type: "boolean", description: "Allow toll roads (global routing option)." },
            ferries: { type: "boolean", description: "Allow ferries (global routing option)." },
            highways: { type: "boolean", description: "Allow highways (global routing option)." }
          }
        }
      }
    ];
  }

  // ---------------------------------------------------------------- settings

  private async setVehicleSettings(args: any): Promise<VehicleToolResult> {
    const baseSettings = this.vehicleService.getECarSettings();
    if (!baseSettings) {
      return this.fail('no_vehicle', 'No vehicle settings are loaded yet.');
    }
    const carType = this.vehicleService.getSelectedCar();

    const { settings, chargerTypes, applied, ignored } = this.mergeSettings(baseSettings, carType, args);
    if (chargerTypes && chargerTypes.length === 0) {
      return this.fail('invalid_charger_types', 'At least one plug type must stay selected.');
    }

    const routingChanged = this.applyRouting(args, applied);
    const vehicleChanged = applied.some(key => !['toll_roads', 'ferries', 'highways'].includes(key));

    if (vehicleChanged) {
      await this.applyToActiveVehicle(settings, chargerTypes, settings.batteryLevel !== baseSettings.batteryLevel);
    }

    if (!vehicleChanged && !routingChanged) {
      return this.fail('nothing_to_change', ignored.length > 0 ? `Nothing changed: ${ignored.join('; ')}.` : 'No setting was given.');
    }

    return {
      output: {
        success: true,
        applied,
        ignored: ignored.length > 0 ? ignored : undefined,
        settings: this.describeSettings(this.vehicleService.getECarSettings(), this.vehicleService.getChargingStations())
      },
      stateChanged: true
    };
  }

  /** Validates and clamps the requested values onto a copy of the settings. */
  private mergeSettings(base: ECarSettings, carType: ECarType | null | undefined, args: any)
    : { settings: ECarSettings, chargerTypes: number[] | null, applied: string[], ignored: string[] } {
    const settings: ECarSettings = { ...base };
    const applied: string[] = [];
    const ignored: string[] = [];

    const numeric = (key: RangeKey, apply: (value: number) => void, decimals: number = 0, raw: unknown = args[key]) => {
      if (raw == null) {
        return;
      }
      const value = Number(raw);
      if (!Number.isFinite(value)) {
        ignored.push(`${key} is not a number`);
        return;
      }
      const factor = Math.pow(10, decimals);
      const clamped = Math.min(RANGES[key][1], Math.max(RANGES[key][0], Math.round(value * factor) / factor));
      if (clamped !== value) {
        ignored.push(`${key} limited to ${clamped} (allowed ${RANGES[key][0]}-${RANGES[key][1]})`);
      }
      apply(clamped);
      applied.push(key);
    };

    numeric('battery_level', v => settings.batteryLevel = v);
    numeric('passengers', v => settings.weight = v);
    numeric('tire_pressure_bar', v => settings.tirePressure = v, 1);
    numeric('speed_limiter_kmh', v => settings.speedLimiter = v);
    numeric('min_battery_at_destination', v => settings.minBatteryAtDestination = v);
    if (args.battery_reserve != null) {
      // Like the dialog, which only shows the reserve slider of the current mode.
      const reserveMode = args.battery_reserve_mode === 'range' || args.battery_reserve_mode === 'route'
        ? args.battery_reserve_mode
        : this.reserveModeForDisplay();
      const key: RangeKey = reserveMode === 'range' ? 'battery_reserve_range_mode' : 'battery_reserve_route_mode';
      numeric(key, v => {
        if (reserveMode === 'range') {
          settings.batterySafetyLimitRha = v;
        } else {
          settings.batterySafetyLimitRoute = v;
        }
      }, 0, args.battery_reserve);
    }
    numeric('battery_state_of_health', v => settings.batteryStateOfHealth = v);

    if (args.fast_charge_limit != null) {
      if (carType && carType.FastChargePower === 0) {
        ignored.push('fast_charge_limit: this car does not support fast charging');
      } else {
        numeric('fast_charge_limit', v => settings.fastChargeLimit = v);
      }
    }

    if (args.driving_style != null) {
      if (args.driving_style in DRIVING_STYLES) {
        settings.userBehavior = DRIVING_STYLES[args.driving_style];
        applied.push('driving_style');
      } else {
        ignored.push(`driving_style must be one of ${Object.keys(DRIVING_STYLES).join(', ')}`);
      }
    }

    if (typeof args.trip_recording === 'boolean') {
      settings.tripRecording = args.trip_recording ? 1 : 0;
      applied.push('trip_recording');
    }

    let chargerTypes: number[] | null = null;
    if (Array.isArray(args.charger_types)) {
      const names = args.charger_types.map((name: unknown) => String(name).toUpperCase().trim());
      chargerTypes = CHARGER_TYPES
        .map((name, index) => names.includes(name) ? index + 1 : 0)
        .filter(id => id > 0);
      const unknown = names.filter((name: string) => !CHARGER_TYPES.includes(name));
      if (unknown.length > 0) {
        ignored.push(`unknown plug types ${unknown.join(', ')}`);
      }
      applied.push('charger_types');
    }

    return { settings, chargerTypes, applied, ignored };
  }

  /** Toll roads / ferries / highways are global settings, saved like the dialog's routing tab. */
  private applyRouting(args: any, applied: string[]): boolean {
    const current = this.settingsService.getRouting();
    const next = {
      tollroads: typeof args.toll_roads === 'boolean' ? args.toll_roads : current.TollRoads,
      ferries: typeof args.ferries === 'boolean' ? args.ferries : current.Ferries,
      highways: typeof args.highways === 'boolean' ? args.highways : current.Highways
    };
    (['toll_roads', 'ferries', 'highways'] as const).forEach(key => {
      if (typeof args[key] === 'boolean') {
        applied.push(key);
      }
    });
    if (next.tollroads === current.TollRoads && next.ferries === current.Ferries && next.highways === current.Highways) {
      return false;
    }

    if (this.settingsService.acceptCookies || this.accountService.getIsAuthorized()) {
      this.settingsService.setRounting(this.settingsService.routingJSONtoRoutingSettings(next));
    }
    if (this.accountService.getIsAuthorized()) {
      this.accountService.setUserSettings().subscribe();
    }
    return true;
  }

  /** Applies settings to the car in use (triggers the recalculation) and persists them. */
  private async applyToActiveVehicle(settings: ECarSettings, chargerTypes: number[] | null, batteryChanged: boolean): Promise<void> {
    if (this.isNavigating()) {
      // Same as moving a vehicle panel slider during navigation: keeps the navigation's own
      // battery prediction from overwriting the value the user just set.
      this.navigationService.setSlidersValueChangedIn15seconds(true);
      if (this.navigationSliderHoldRef) {
        clearTimeout(this.navigationSliderHoldRef);
      }
      this.navigationSliderHoldRef = setTimeout(() => this.navigationService.setSlidersValueChangedIn15seconds(false), NAVIGATION_SLIDER_HOLD_MS);
    }

    // VehicleService keeps batterySafetyLimit pointing at the reserve of the current mode.
    settings.batterySafetyLimit = this.waypointService.getSelectedDisplayMode() === DisplayMode.ROUTE
      ? settings.batterySafetyLimitRoute
      : settings.batterySafetyLimitRha;

    if (chargerTypes) {
      this.vehicleService.setChargingStations(this.chargerTypeFlags(chargerTypes));
    }

    const authorized = this.accountService.getIsAuthorized();
    const userCar = authorized ? this.accountService.getUserProfile()?.getSelectedUserCar() : null;
    if (userCar) {
      // Update the cached copy first, so the battery observer below sees no difference to save.
      userCar.settings = { ...settings };
      if (chargerTypes && userCar.ECarType) {
        userCar.ECarType.ChargerTypes = chargerTypes;
      }
    } else if (chargerTypes) {
      this.vehicleService.getSelectedCar().ChargerTypes = chargerTypes;
    }

    this.vehicleService.setECarSettings({ ...settings });
    if (batteryChanged) {
      // Moves the vehicle panel's battery slider.
      this.vehicleService.ObservableBatteryChanged.next(settings.batteryLevel);
    }

    if (userCar) {
      await firstValueFrom(this.accountService.editVehicle(userCar.userVehicleID, null, null, chargerTypes, JSON.stringify(settings), false));
    } else {
      this.persistAnonymousSettings(settings);
    }
  }

  /** The localStorage keys the vehicle options dialog and vehicle panel write for signed-out users. */
  private persistAnonymousSettings(settings: ECarSettings): void {
    if (!this.settingsService.acceptCookies) {
      return;
    }
    const values: Record<string, unknown> = {
      batteryLevel: settings.batteryLevel,
      batterySafetyLimitRha: settings.batterySafetyLimitRha,
      batterySafetyLimitRoute: settings.batterySafetyLimitRoute,
      minBatteryAtDestination: settings.minBatteryAtDestination,
      speedLimiter: settings.speedLimiter,
      tirePressure: settings.tirePressure,
      userBehavior: settings.userBehavior,
      weight: settings.weight,
      tripRecording: settings.tripRecording,
      batteryStateOfHealth: settings.batteryStateOfHealth,
      fastChargeLimit: settings.fastChargeLimit,
      chargingStations: this.vehicleService.getChargingStations()
    };
    Object.entries(values).forEach(([key, value]) => {
      if (value != null) {
        localStorage.setItem(key, value.toString());
      }
    });
  }

  // ---------------------------------------------------------------- helpers

  private describeSettings(settings: ECarSettings | null, chargerFlags: boolean[]): Record<string, unknown> | null {
    if (!settings) {
      return null;
    }
    const routing = this.settingsService.getRouting();
    const drivingStyle = Object.entries(DRIVING_STYLES)
      .reduce((best, entry) => Math.abs(entry[1] - settings.userBehavior) < Math.abs(best[1] - settings.userBehavior) ? entry : best)[0];

    return {
      battery_level: settings.batteryLevel,
      passengers: settings.weight,
      tire_pressure_bar: settings.tirePressure,
      driving_style: drivingStyle,
      speed_limiter_kmh: settings.speedLimiter,
      min_battery_at_destination: settings.minBatteryAtDestination,
      battery_reserve_current_mode: this.reserveModeForDisplay(),
      battery_reserve_range_mode: settings.batterySafetyLimitRha,
      battery_reserve_route_mode: settings.batterySafetyLimitRoute,
      battery_state_of_health: settings.batteryStateOfHealth ?? 100,
      fast_charge_limit: settings.fastChargeLimit,
      charger_types: CHARGER_TYPES.filter((_, i) => chargerFlags[i]),
      trip_recording: (settings.tripRecording ?? 1) === 1,
      toll_roads: routing.TollRoads,
      ferries: routing.Ferries,
      highways: routing.Highways
    };
  }

  private chargerTypeFlags(chargerTypes: number[]): boolean[] {
    return CHARGER_TYPES.map((_, i) => chargerTypes.includes(i + 1));
  }

  /** Which mode's reserve the user is looking at: range mode uses its own, everything else plans routes. */
  private reserveModeForDisplay(): 'range' | 'route' {
    return this.waypointService.getSelectedDisplayMode() === DisplayMode.RANGE ? 'range' : 'route';
  }

  private isNavigating(): boolean {
    return this.waypointService.getSelectedDisplayMode() === DisplayMode.NAVIGATION;
  }

  private fail(error: string, message: string): VehicleToolResult {
    return { output: { success: false, error, message }, stateChanged: false };
  }
}
